import React, { lazy, Suspense, useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import {
  Button,
  Card,
  Input,
  Select,
  Toast,
  Typography,
  useCheckBox,
  useInput,
  useSelect,
  useViewport
} from '@chilecompra/react-kit/components'
import { checkInputErrorMaxLength, checkInputErrorMinLength, Spinner, useEffectOnce } from '@chilecompra/react-kit'
import {
  checkInputErrorEmail,
  checkInputErrorOnlyNumbers,
  checkInputErrorRequired,
  formatDNI,
  formatOnlyNumbers
} from '@chilecompra/react-kit/utils'
import { PHONE_NUMBERS_STARTS_WITH, POSITION_OPTIONS } from '../../../config/settings/constants'
import {
  REACT_APP_ATTACHMENT_API_URL,
  REACT_APP_BUSINESS,
  REACT_APP_HELP_REPRESENTATIVE_AUTHORITY_URL,
  REACT_APP_ID_BUSINESS,
  REACT_APP_SUB_BUSINESS
} from '../../../config/settings/environment'
import { checkInputErrorStartsWith, formatForeignDocument, formatOnlyLetters } from '../../../modules/utils/checkers'
import ProtectByFeatures from '../../../components/Security/ProtectByFeatures'
import useRequestInterceptor from '../../../modules/hooks/useRequestInterceptor'
import {
  getRepresentativeAuthorityThunk,
  onGetDownloadFileThunk,
  onResetRegisterWizardState,
  onSaveTemporaryAuthorityInformation,
  registerFlowThunk
} from '../RegisterWizard.actions'
import {
  AttachmentSection,
  AttachmentSubTitle,
  CheckBox,
  DesktopNavigation,
  Divider,
  Form,
  Hidden,
  Image,
  Instructions,
  Link,
  MobileNavigation,
  Root,
  Search,
  PhoneNumberPrefix,
  SearchClearIcon,
  SearchIcon,
  SearchLoadingIcon,
  Section,
  Strong,
  Title,
  ToastContainer,
  AttachmentButtonSection,
  DeleteTypography,
  DeleteIcon,
  StrongToast
} from './RegisterWizardRepresentativeAuthority.styles'

const Attachments = lazy(() => import('remote/FrontAdjuntos'))

/**
 * The RegisterWizardRepresentativeAuthority' container.
 */
const RegisterWizardRepresentativeAuthority = props => {
  const { onChangeStep } = props
  const dispatch = useDispatch()
  const { size } = useViewport()
  const flow = 'INICIO_PASO_4'

  const [clear, setClear] = useState(false)
  const [isForeignerChecked, setIsForeignerChecked] = useState(false)
  const [showForeignerSection, setIsShowForeignerSection] = useState(false)
  const [openModalAttachResolution, setOpenModalAttachResolution] = useState(false)
  const [openModalDeleteAllResolution, setOpenModalDeleteAllResolution] = useState(false)
  const [files, setFiles] = useState([])

  const downloadFileUrl = useSelector(state => state.registerWizard.downloadFileUrl)
  const { names, fatherLastName, motherLastName, loading, error } = useSelector(
    state => state.registerWizard.representativeAuthority
  )

  useRequestInterceptor(
    `${REACT_APP_ATTACHMENT_API_URL}/adjuntos-api/${REACT_APP_BUSINESS}/${REACT_APP_SUB_BUSINESS}/${REACT_APP_ID_BUSINESS}`,
    setFiles
  )

  const handleNext = () => {
    dispatch(
      onSaveTemporaryAuthorityInformation({
        lastName: lastNameValue,
        name: nameValue,
        phone: phoneValue,
        phoneCode: '56',
        email: emailValue,
        dni: dniValue,
        position: positionValue,
        isUniquePass: isForeignerChecked ? 0 : 1,
        files
      })
    )
    onChangeStep('collaboration-agreement')
  }

  const handleShowForeignerSection = () => setIsShowForeignerSection(true)

  const { value: checked, onChange: handleChangeCheckbox } = useCheckBox()

  const {
    error: dniError,
    value: dniValue,
    onChange: handleChange,
    setValue: setDNIValue
  } = useInput({
    initialValue: '',
    changeCallback: () => setClear(false),
    formatCallbacks: [isForeignerChecked ? formatForeignDocument : formatDNI],
    overwriteCallback: () => setClear(false),
    errorCallbacks: [checkInputErrorRequired()]
  })

  const {
    error: nameError,
    value: nameValue,
    onChange: handleNameChange,
    setValue: setNameValue
  } = useInput({
    initialValue: '',
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLetters]
  })

  const {
    error: lastNameError,
    value: lastNameValue,
    onChange: handleLastNameChange,
    setValue: setLastNameValue
  } = useInput({
    initialValue: '',
    errorCallbacks: [checkInputErrorRequired()],
    formatCallbacks: [formatOnlyLetters]
  })

  const {
    error: phoneError,
    value: phoneValue,
    onChange: handlePhoneChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired(),
      checkInputErrorStartsWith('El número debe comenzar con 9 o 2.', PHONE_NUMBERS_STARTS_WITH),
      checkInputErrorOnlyNumbers(),
      checkInputErrorMinLength(9, 'Este campo permite 8 dígitos.'),
      checkInputErrorMaxLength(9, 'Este campo permite 8 dígitos.')
    ],
    formatCallbacks: [formatOnlyNumbers]
  })

  const {
    error: positionError,
    value: positionValue,
    onChange: handlePositionChange
  } = useSelect({
    initialValue: 'Seleccione una opción',
    overwriteCallback: () => [checkInputErrorRequired()]
  })

  const {
    error: emailError,
    value: emailValue,
    onChange: handleEmailChange
  } = useInput({
    errorCallbacks: [
      checkInputErrorRequired('Debes ingresar tu dirección de correo.'),
      checkInputErrorEmail('Tu dirección de correo parece estar en un formato incorrecto.'),
      checkInputErrorMaxLength(80, 'Tu dirección de correo excede el máximo de 80 carácteres permitidos.')
    ],
    initialValue: ''
  })

  const makeSubmission = () => {
    dispatch(getRepresentativeAuthorityThunk({ dni: dniValue }))
    setClear(false)
  }

  const handleClick = () => {
    if (!clear) {
      return makeSubmission()
    }
    setDNIValue('')
    setClear(false)
  }

  const handleBack = () => {
    dispatch(onResetRegisterWizardState())
    onChangeStep('welcome')
  }

  const handleSubmit = event => {
    event.preventDefault()
    makeSubmission()
  }

  const handleClickChekbox = () => setIsForeignerChecked(!isForeignerChecked)

  const canNotSubmit = useCallback(() => {
    const hadErrors =
      nameError !== '' || lastNameError !== '' || phoneError !== '' || positionError !== '' || dniError !== ''

    const hadInvalidValues =
      phoneValue === '' || positionValue === '' || nameValue === '' || lastNameValue === '' || dniValue === ''

    const noFilesUploaded = files.length === 0

    return hadErrors || hadInvalidValues || noFilesUploaded
  }, [
    dniError,
    lastNameError,
    nameError,
    phoneError,
    positionError,
    nameValue,
    lastNameValue,
    phoneValue,
    positionValue,
    dniValue,
    files
  ])

  useEffectOnce(() => {
    dispatch(registerFlowThunk({ flow }))
  }, [])

  const fetchDownloadUrl = useCallback(() => {
    if (files.length > 0 && files[0].iddoc) {
      dispatch(onGetDownloadFileThunk({ iddoc: files[0].iddoc }))
    }
  }, [files, dispatch])

  const handleDeleteFile = () => {
    setFiles([])
  }

  useEffect(() => {
    if (names || fatherLastName || motherLastName) {
      setNameValue(names || '')
      setLastNameValue(`${fatherLastName || ''}${fatherLastName ? ' ' : ''}${motherLastName || ''}`)
    }
  }, [names, fatherLastName, motherLastName, setLastNameValue, setNameValue])

  useEffect(() => {
    if (files.length > 0 && files[0].filename) {
      fetchDownloadUrl()
    }
  }, [files, fetchDownloadUrl])

  const disabledSubmit = nameValue && canNotSubmit()
  const isAttachmentButtonDisabled = files.length > 0

  const renderToast = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          disableIcon
          margin={size.isTiny ? '20px 0 20px 0' : size.isDownToLarge ? '18px 0 0' : '53px 0 0'}
          severity="default"
          width={size.isTiny ? '90%' : size.isDownToLarge ? '100%' : '263px'}
        >
          <StrongToast tabIndex="0">¿Qué se entiende por autoridad con poder de representación?</StrongToast>
          <Divider />
          <span tabIndex="0">
            Es la persona natural que tiene la facultad para obligar a la institución que representa, a través de su
            firma. Por ejemplo, en una municipalidad, es el alcalde electo, mientras que en una corporación municipal,
            el director de la misma.
          </span>
          <Divider />
          <Link
            color="primary"
            tabIndex="0"
            aria-label="Obtener más ayuda"
            underline
            href={REACT_APP_HELP_REPRESENTATIVE_AUTHORITY_URL}
            target="_blank"
            rel="noreferrer"
          >
            Obtener más ayuda
          </Link>
        </Toast>
      </ToastContainer>
    )
  }

  const renderToastFields = () => {
    return (
      <ToastContainer role="alert" aria-live="assertive">
        <Toast
          tabIndex="0"
          disableIcon
          margin={size.isTiny ? '20px 0 0' : size.isDownToLarge ? '18px 0 0' : '15px 0 0'}
          severity="default"
        >
          Todos los campos son obligatorios. Sin perjuicio de lo anterior, solo serán visibles públicamente en la ficha
          de tu institución los datos relativos a nombre, apellido y cargo de la autoridad representante. El correo
          electrónico y el teléfono laboral son datos necesarios para la Dirección ChileCompra.
        </Toast>
      </ToastContainer>
    )
  }

  return (
    <Root>
      <Section
        display="flex"
        flexDirection={size.isTiny ? 'column' : 'row'}
        justifyContent="space-between"
        gap={size.isDownToLarge ? '' : '50px'}
      >
        <Section>
          <Section item>
            <Title tabIndex="0" aria-label="Paso 4: Autoridad representante">
              Autoridad representante
            </Title>
          </Section>
          <Section
            item
            margin={size.isTiny ? '10px 0 30px 0' : size.isDownToLarge ? '0 0 30px 0' : '24px 0 20px 0'}
            maxWidth="632px"
          >
            <Instructions tabIndex="0">
              Ingresa la información de contacto correspondiente para quién sea <Strong>actualmente</Strong> la persona
              natural que actúa como autoridad representante de la institución. Esta información puede ser gestionada y
              actualizada más adelante en Mercado Público.{' '}
            </Instructions>
          </Section>
          {!showForeignerSection && (
            <Section item display="flex">
              <Form onSubmit={handleSubmit}>
                <Input
                  autoFocus
                  disabled={loading || isForeignerChecked}
                  label="RUT"
                  suffix={
                    <Search
                      tabIndex="0"
                      aria-label="Buscar Rut"
                      clear={clear && !loading}
                      disabled={dniValue === '' || loading}
                      type="button"
                      onClick={handleClick}
                    >
                      {!clear && !loading && <SearchIcon />}
                      {loading && <SearchLoadingIcon />}
                      {clear && !loading && <SearchClearIcon />}
                    </Search>
                  }
                  value={dniValue}
                  onChange={handleChange}
                />
              </Form>
            </Section>
          )}

          {isForeignerChecked && showForeignerSection && (
            <Section item display="flex">
              <Form onSubmit={handleSubmit}>
                <Input
                  autoFocus
                  error={dniError}
                  disabled={loading}
                  label="Número de identificación nacional (DNI, Pasaporte, etc)"
                  aria-label="Número de identificación nacional (DNI, Pasaporte, etc)"
                  value={dniValue}
                  onChange={handleChange}
                />
              </Form>
            </Section>
          )}

          {error && error === 'SOMETHING_WENT_WRONG_ERROR' && !loading && (
            <Toast
              tabIndex="0"
              width={size.isTiny ? '' : size.isDownToLarge ? '' : '70%'}
              severity="warning"
              padding="16px 24px"
            >
              En estos momentos no podemos acceder a la información debido a un problema de conexión con el Registro
              Civil, puedes intentar nuevamente en unos minutos para continuar con tu inscripción.
            </Toast>
          )}

          {error && error === 'UNDERAGE_USER_ERROR' && !loading && (
            <Toast
              tabIndex="0"
              width={size.isTiny ? '' : size.isDownToLarge ? '' : '70%'}
              severity="warning"
              padding="16px 24px"
            >
              El Rut ingresado corresponde a una persona menor de edad según la información del Registro Civil.
            </Toast>
          )}

          {error && error === 'DECEASED_PERSON_ERROR' && !loading && (
            <Toast
              tabIndex="0"
              width={size.isTiny ? '' : size.isDownToLarge ? '' : '70%'}
              severity="warning"
              padding="16px 24px"
            >
              El Rut ingresado corresponde a una persona fallecida según la información del Registro Civil.
            </Toast>
          )}

          <ProtectByFeatures rule={features => features['com.buyer/foreign.authority']}>
            {!nameValue && !loading && !error && !showForeignerSection && (
              <Section display="flex" alignItems="center">
                <CheckBox
                  checked={checked}
                  tabIndex="0"
                  aria-label="La autoridad representante es una persona extranjera sin RUT Chileno"
                  onClick={handleClickChekbox}
                  onChange={handleChangeCheckbox}
                  color="primary"
                />
                <Typography variant="body2" textAlign="left">
                  La autoridad representante es una persona extranjera sin RUT Chileno
                </Typography>
              </Section>
            )}
          </ProtectByFeatures>

          {(nameValue || (isForeignerChecked && showForeignerSection)) && !loading && !error && (
            <Section container direction="column">
              <Section item>
                <Input
                  disabled={!isForeignerChecked}
                  error={nameError}
                  label="Nombres"
                  aria-label="Nombres"
                  loading={loading}
                  onChange={handleNameChange}
                  value={nameValue}
                />
              </Section>
              <Section item>
                <Input
                  disabled={!isForeignerChecked}
                  error={lastNameError}
                  label="Apellidos"
                  aria-label="Apellidos"
                  loading={loading}
                  onChange={handleLastNameChange}
                  value={lastNameValue}
                />
              </Section>
              <Section item>
                <Select
                  label="Cargo"
                  aria-label="Cargo"
                  error={positionError}
                  loading={loading}
                  options={POSITION_OPTIONS}
                  value={positionValue}
                  onChange={handlePositionChange}
                />
              </Section>
              <Section item>
                <Input
                  error={emailError}
                  loading={loading}
                  label="Correo electrónico laboral"
                  aria-label="Correo electrónico laboral"
                  value={emailValue}
                  onChange={handleEmailChange}
                />
              </Section>
              <Section item>
                <Input
                  prefix={<PhoneNumberPrefix>+56</PhoneNumberPrefix>}
                  error={phoneError}
                  label="Número de teléfono laboral o móvil"
                  aria-label="Número de teléfono laboral o móvil"
                  onChange={handlePhoneChange}
                  placeholder="981234567"
                  value={phoneValue}
                />
              </Section>
              {!loading && renderToastFields()}

              <AttachmentSection disabled={isAttachmentButtonDisabled}>
                <Section tabIndex="0" maxWidth={size.isTiny ? '312px' : '216px'} margin={size.isTiny && '100px 0 0 0'}>
                  <Typography variant="body1" fontWeight="bold">
                    Adjuntar documento designación autoridad
                  </Typography>
                  <Typography margin="5px 0" variant="body2">
                    Puedes subir archivos u escrituras en donde se nombre a la autoridad representante de la
                    institución.
                  </Typography>
                  <AttachmentSubTitle>Se admiten archivos con un peso máximo de 20mb.</AttachmentSubTitle>
                </Section>
                <AttachmentButtonSection margin={size.isTiny && '10px 0'}>
                  <Suspense fallback={<Spinner />}>
                    <Attachments
                      business={REACT_APP_BUSINESS}
                      idBusiness={REACT_APP_ID_BUSINESS}
                      subBusiness={REACT_APP_SUB_BUSINESS}
                      color="blank"
                      typeView="list"
                      openModal={openModalAttachResolution}
                      openModalDeleteAll={openModalDeleteAllResolution}
                      setOpenModal={setOpenModalAttachResolution}
                      setOpenModalDeleteAll={setOpenModalDeleteAllResolution}
                      WRITE
                      LIST
                      WRITEBULK
                      DELETEBULK={false}
                      DELETE={false}
                      READ={false}
                      READBULK={false}
                    />
                  </Suspense>
                </AttachmentButtonSection>
              </AttachmentSection>
            </Section>
          )}

          {files.length > 0 && (
            <Card color="blank" enableBorder margin={size.isTiny || size.isDownToLarge ? '' : '20px 0 0 0'} zIndex={1}>
              <Section display="flex" justifyContent="space-between" alignItems="center">
                <Section>
                  {files && files.length > 0 && (
                    <Typography variant="body2">
                      <Link
                        tabIndex="0"
                        aria-label={`Descargar el archivo ${files[0].filename}`}
                        href={downloadFileUrl}
                      >
                        {files[0].filename}
                      </Link>
                    </Typography>
                  )}
                </Section>
                <Section
                  tabIndex="0"
                  aria-label="Eliminar archivo"
                  onClick={handleDeleteFile}
                  display="flex"
                  alignItems="center"
                  gap="7px"
                  margin="0 20px 0 0"
                >
                  <DeleteTypography>Eliminar</DeleteTypography>
                  <DeleteIcon />
                </Section>
              </Section>

              <Section />
            </Card>
          )}

          {loading && (
            <Section
              role="status"
              aria-label="Cargando..."
              display="flex"
              justifyContent="center"
              alignItems="center"
              marginTop={size.isTiny ? '30px' : size.isDownToLarge ? '20px' : '50px'}
            >
              <Spinner />
            </Section>
          )}
          {(size.isTiny || size.isDownToLarge) && !loading && !nameValue && !isForeignerChecked && renderToast()}
        </Section>

        <Section display="flex" flexDirection="column">
          <Hidden lgDown>
            <Image src="/assets/img/steps/paso5.svg" alt="Inscribir una nueva institución en Mercado Público" />
          </Hidden>
          {!size.isTiny && !size.isDownToLarge && renderToast()}
        </Section>
      </Section>

      {!error && !size.isTiny && (
        <Section margin="52px 0 0 0">
          <DesktopNavigation>
            <Button
              tabIndex="0"
              aria-label="Cancelar"
              type="button"
              variant="outlined"
              width={size.isDownToLarge ? '182px' : '156px'}
              onClick={handleBack}
            >
              Cancelar
            </Button>
            {(!checked || showForeignerSection) && (
              <Button
                tabIndex="0"
                aria-label="Siguiente"
                disabled={loading || disabledSubmit || (!nameValue && !checked) || (!nameValue && checked)}
                color="primary"
                type="button"
                width={size.isDownToLarge ? '182px' : '156px'}
                onClick={handleNext}
              >
                Siguiente
              </Button>
            )}

            {checked && !showForeignerSection && (
              <Button
                tabIndex="0"
                aria-label="Siguiente"
                disabled={loading || disabledSubmit}
                color="primary"
                margin="0 0 0 30px"
                type="button"
                width={size.isDownToLarge ? '182px' : '156px'}
                onClick={handleShowForeignerSection}
              >
                Siguiente
              </Button>
            )}
          </DesktopNavigation>
        </Section>
      )}
      {!error && size.isTiny && (
        <MobileNavigation>
          <Button tabIndex="0" aria-label="Cancelar" type="button" variant="outlined" onClick={handleBack}>
            Cancelar
          </Button>
          {(!checked || showForeignerSection) && (
            <Button
              tabIndex="0"
              aria-label="Siguiente"
              disabled={loading || disabledSubmit || (!nameValue && !checked) || (!nameValue && checked)}
              color="primary"
              type="button"
              onClick={handleNext}
            >
              Siguiente
            </Button>
          )}

          {checked && !showForeignerSection && (
            <Button
              tabIndex="0"
              aria-label="Siguiente"
              disabled={loading || disabledSubmit}
              color="primary"
              type="button"
              onClick={handleShowForeignerSection}
            >
              Siguiente
            </Button>
          )}
        </MobileNavigation>
      )}

      {error && !loading && !size.isTiny && (
        <Section container margin="52px 0 0 0">
          <DesktopNavigation>
            <Button
              tabIndex="0"
              aria-label="Cancelar"
              type="button"
              variant="outlined"
              width={size.isDownToLarge ? '182px' : '156px'}
              onClick={handleBack}
            >
              Cancelar
            </Button>
          </DesktopNavigation>
        </Section>
      )}

      {error && !loading && size.isTiny && (
        <Section>
          <MobileNavigation>
            <Button tabIndex="0" aria-label="Cancelar" type="button" variant="outlined" onClick={handleBack}>
              Cancelar
            </Button>
          </MobileNavigation>
        </Section>
      )}
    </Root>
  )
}

RegisterWizardRepresentativeAuthority.defaultProps = {
  onChangeStep: () => undefined
}

RegisterWizardRepresentativeAuthority.propTypes = {
  onChangeStep: PropTypes.func
}

export default RegisterWizardRepresentativeAuthority
